import React, { useEffect, useState } from "react";
import { Crown, TrendingUp, Info } from "lucide-react";
import ManagePaymentModal from "./ManagePaymentModal";
import {
  findPlanDefinition,
  getPlanKey,
  isSubscriptionPlan,
  getBillingPeriodLabel,
} from "../../assets/data/pricingData";
import PricingModal from "./PricingModal";
import Cookies from "js-cookie";

const Pricing = () => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [projects, setProjects] = useState([]);
  const [planDetails, setPlanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [autoPay, setAutoPay] = useState(false);
  const [visiblePayments, setVisiblePayments] = useState(5);
  const displayedPayments = paymentHistory.slice(0, visiblePayments);

  const allPaymentsVisible = visiblePayments >= paymentHistory.length;
  const INVITE_ONLY_PLANS = ["Freemium", "Custom"];

  const findPlanByName = (planName, orgPlanData) => {
    if (!planName) return null;
    if (planName === "Free") {
      return {
        name: "Free",
        priceDisplay: "$0",
        period: "/month",
        features: [
          { text: "180,000 Research Units (RUs)" },
          { text: "3 Monthly Projects" },
          { text: "120 Avatar Questions / month" },
          { text: "Banner Book Included" },
          { text: "Summary Findings Report Included" },
        ],
      };
    }
    if (INVITE_ONLY_PLANS.includes(planName)) {
      const totalCredits = orgPlanData?.total_credits_score ?? 0;
      const totalProjects = orgPlanData?.total_project_limits ?? 0;
      const totalQuestions = orgPlanData?.total_question_count;
      return {
        name: planName,
        priceDisplay: planName,
        period: "",
        isInviteType: true,
        features: [
          { text: `${totalCredits.toLocaleString()} Research Units (RUs)` },
          {
            text: `${totalProjects} Monthly Project${totalProjects === 1 ? "" : "s"}`,
          },
          {
            text:
              totalQuestions === null || totalQuestions === undefined
                ? "Unlimited Avatar Questions"
                : `${totalQuestions.toLocaleString()} Avatar Questions / month`,
          },
        ],
      };
    }
    return findPlanDefinition(planName);
  };

  useEffect(() => {
    const fetchUserPlan = async () => {
      const token = Cookies.get("token");
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/project/getuserPlanAndProject`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", token: token },
            credentials: "include",
          },
        );

        const data = await response.json();

        if (data.status === "success") {
          setUserPlan(data.planDetails);

          const validProjects = data.projectAllowed.filter(
            (proj) =>
              proj.status &&
              (proj.status === "completed" || proj.status === "running"),
          );
          setProjects(validProjects);
          const matchedPlan = findPlanByName(
            data.planDetails.plan_name,
            data.planDetails,
          );
          if (matchedPlan) {
            setPlanDetails(matchedPlan);
          } else {
            console.warn("Plan not found in JSON:", data.planDetails.plan_name);
          }
          if (!INVITE_ONLY_PLANS.includes(data.planDetails.plan_name)) {
            try {
              const subResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/payment/subscription-status`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    token: token,
                  },
                  credentials: "include",
                },
              );
              const subData = await subResponse.json();
              if (subData.success) {
                setAutoPay(subData.data.autopayEnabled);
              }
            } catch (err) {
              console.error("Error fetching autopay status:", err);
            }
          }
        }

        if (data.auditsDetails && Array.isArray(data.auditsDetails)) {
          const payments = data.auditsDetails
            .map((audit) => {
              const accountType = (
                audit.updatedBy?.accountType || ""
              ).toLowerCase();
              const isInviteType =
                accountType === "freemium" || accountType === "custom";
              const inviteLabel =
                accountType.charAt(0).toUpperCase() + accountType.slice(1);
              const planLabel =
                audit.plan_name || (isInviteType ? inviteLabel : "—");
              const adminName =
                [audit.updatedBy?.first_name, audit.updatedBy?.last_name]
                  .filter(Boolean)
                  .join(" ") ||
                audit.updatedBy?.email ||
                "—";

              return {
                plan: planLabel,
                adminName,
                date: new Date(
                  audit.updatedAt || audit.create_date,
                ).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }),
                amount:
                  isInviteType && !audit.totalAmount
                    ? "$0"
                    : `$${(audit.totalAmount || 0).toLocaleString()}`,
                status:
                  isInviteType && !audit.totalAmount ? "no charge" : "paid",
                _hasAmount: !!audit.totalAmount,
                _isInviteType: isInviteType,
              };
            })
            .filter((payment) => payment._hasAmount || payment._isInviteType)
            .map(({ _hasAmount, _isInviteType, ...rest }) => rest)
            .reverse();

          setPaymentHistory(payments);
        }
      } catch (err) {
        console.error("Error fetching plan info:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserPlan();
  }, []);
  const [payLoading, setPayLoading] = useState(false);
  const handlePayNow = async () => {
    setPayLoading(true);
    const token = Cookies.get("token");
    try {
      const planKey = getPlanKey(userPlan?.plan_name) || userPlan?.plan_name;

      localStorage.setItem(
        "paymentContext",
        JSON.stringify({ source: "PRICING_PAGE", planKey }),
      );

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/create-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token },
          credentials: "include",
          body: JSON.stringify({ planKey }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Failed to start payment");
      }
      window.location.href = data.url;
    } catch (err) {
      console.error("Pay Now error:", err);
      alert(err.message || "Failed to start payment. Please try again.");
      setPayLoading(false);
    }
  };

  const handleAutoPayToggle = async () => {
    const token = Cookies.get("token");
    try {
      if (!autoPay) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payment/enable-autopay`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token,
            },
            credentials: "include",
            body: JSON.stringify({
              planKey: getPlanKey(userPlan?.plan_name),
            }),
          },
        );
        const data = await response.json();
        if (data.success) {
          setAutoPay(true);
        } else {
          alert(data.message || "Failed to enable Auto Pay");
        }
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payment/disable-autopay`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token,
            },
            credentials: "include",
            body: JSON.stringify({
              cancelImmediately: false,
            }),
          },
        );
        const data = await response.json();
        if (data.success) {
          setAutoPay(false);
        } else {
          alert(data.message || "Failed to disable Auto Pay");
        }
      }
    } catch (err) {
      console.error("AutoPay toggle failed:", err);
      alert("Something went wrong");
    }
  };

  if (loading) {
    return (
      <div className="pricing-loading">
        <div className="pricing-loading-text">Loading plan details...</div>
      </div>
    );
  }

  const totalProjectsAllowed = userPlan?.total_project_limits || 0;
  const projectsLeft = userPlan?.project_limits;
  const projectUsagePercent = totalProjectsAllowed
    ? Math.min((projectsLeft / totalProjectsAllowed) * 100, 100)
    : 0;

  const creditsLeft = userPlan?.credits_score || 0;
  const totalCredits = userPlan?.total_credits_score || 0;
  const creditUsagePercent = totalCredits
    ? Math.min((creditsLeft / totalCredits) * 100, 100)
    : 0;

  const totalQuestionsAllowed = userPlan?.total_question_count;
  const questionsUsed = userPlan?.used_question_count || 0;
  const questionsLeft =
    totalQuestionsAllowed === null || totalQuestionsAllowed === undefined
      ? null
      : Math.max(0, totalQuestionsAllowed - questionsUsed);
  const questionUsagePercent =
    totalQuestionsAllowed === null || totalQuestionsAllowed === undefined
      ? 100
      : totalQuestionsAllowed > 0
        ? Math.min(
            ((totalQuestionsAllowed - questionsUsed) / totalQuestionsAllowed) *
              100,
            100,
          )
        : 0;

  const isInviteAccount = INVITE_ONLY_PLANS.includes(userPlan?.plan_name);
  const isPaymentPending =
    isInviteAccount && userPlan?.paymentStatus === "pending";
  const hideCreditsBar = isInviteAccount && totalCredits === 0;
  const hideProjectsBar = isInviteAccount && totalProjectsAllowed === 0;
  const hideQuestionsBar =
    totalQuestionsAllowed === null ||
    totalQuestionsAllowed === undefined ||
    totalQuestionsAllowed === 0;

  return (
    <div className="admin-container">
      <div className="pricing-stack">
        {!userPlan || !planDetails ? (
          <div className="pricing-empty">
            <div className="pricing-empty-text">
              You don't have an active plan yet
              <br />
              Choose a plan to unlock and start using Generative Insights.
            </div>
            <button
              className="admin-btn btn-primary"
              onClick={() => setShowPricingModal(true)}
            >
              View Plans
            </button>
          </div>
        ) : (
          <>
            {/* Current Plan Card */}
            <div className="admin-card plan-card">
              <div className="plan-card-inner">
                <div className="plan-card-body">
                  <h2 className="plan-card-title">Current Plan</h2>
                  <p className="plan-card-subtitle">
                    {isPaymentPending
                      ? `Your ${userPlan.plan_name} plan is waiting on payment`
                      : `You're on the ${userPlan.plan_name} plan`}
                  </p>
                  <div className="plan-card-price">
                    <span className="plan-price-amount">
                      {planDetails.priceDisplay}
                    </span>
                    {!planDetails.isInviteType && (
                      <span className="plan-price-period">
                        {planDetails.period || "/year"}
                      </span>
                    )}
                  </div>
                  <div className="plan-features-list">
                    {planDetails.features.slice(0, 3).map((feature, i) => (
                      <PlanFeature key={i} text={feature.text} />
                    ))}
                  </div>
                  <div className="plan-actions">
                    {isPaymentPending ? (
                      <button
                        className="admin-btn btn-primary"
                        onClick={handlePayNow}
                        disabled={payLoading}
                      >
                        {payLoading ? "Redirecting to payment..." : "Pay Now"}
                      </button>
                    ) : (
                      <button
                        className="admin-btn btn-primary"
                        onClick={() => setShowPricingModal(true)}
                      >
                        {userPlan?.plan_name === "Free"
                          ? "Choose a Paid Plan"
                          : "Upgrade Plan"}
                      </button>
                    )}
                    {userPlan?.plan_name !== "Free" &&
                      !INVITE_ONLY_PLANS.includes(userPlan?.plan_name) && (
                        <button
                          className="admin-btn btn-secondary"
                          onClick={() => setShowPaymentModal(true)}
                        >
                          Manage Credits
                        </button>
                      )}
                    {isSubscriptionPlan(userPlan?.plan_name) && (
                      <div className="autopay-toggle-wrapper">
                        <span className="autopay-label">Auto Pay</span>
                        <button
                          className="autopay-info-btn"
                          title="Automatically renew your plan each billing cycle"
                          aria-label="Auto Pay info"
                        >
                          <Info size={14} />
                        </button>
                        <button
                          className={`autopay-toggle ${autoPay ? "autopay-toggle--on" : ""}`}
                          onClick={handleAutoPayToggle}
                          aria-pressed={autoPay}
                          aria-label="Toggle Auto Pay"
                        >
                          <span className="autopay-thumb" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <Crown className="plan-crown-icon" size={56} />
              </div>
            </div>
            <div className="admin-card">
              <div className="usage-card-header">
                <h3 className="usage-card-title">
                  Usage This {getBillingPeriodLabel(userPlan?.plan_name)}
                </h3>
                <TrendingUp size={18} className="usage-trend-icon" />
              </div>

              {!hideCreditsBar && (
                <UsageBar
                  label="RU Credits Remaining"
                  value={`${creditsLeft.toLocaleString()} / ${totalCredits.toLocaleString()}`}
                  width={`${creditUsagePercent}%`}
                  color="blue"
                  subtitle="Credits left in your plan"
                />
              )}
              {!hideProjectsBar && (
                <UsageBar
                  label="Projects Remaining"
                  value={`${projectsLeft} / ${totalProjectsAllowed}`}
                  width={`${projectUsagePercent}%`}
                  color="purple"
                  subtitle="Project slots available"
                />
              )}
              {!hideQuestionsBar && (
                <UsageBar
                  label="Avatar Questions Remaining"
                  value={`${questionsLeft.toLocaleString()} / ${totalQuestionsAllowed.toLocaleString()}`}
                  width={`${questionUsagePercent}%`}
                  color="green"
                  subtitle="Questions left for avatar interactions"
                />
              )}

              {isPaymentPending && (
                <div className="usage-alert usage-alert--danger">
                  Payment not completed yet. Your credits, projects, and avatar
                  questions will unlock as soon as payment goes through.{" "}
                  <button
                    className="usage-alert-inline-link"
                    onClick={handlePayNow}
                    disabled={payLoading}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      textDecoration: "underline",
                      cursor: "pointer",
                      color: "inherit",
                      font: "inherit",
                    }}
                  >
                    {payLoading ? "Redirecting..." : "Pay Now"}
                  </button>
                </div>
              )}

              {!hideCreditsBar &&
                creditUsagePercent < 20 &&
                creditsLeft > 0 && (
                  <div className="usage-alert usage-alert--warning">
                    You're running low on credits. Consider purchasing more
                    credits.
                  </div>
                )}
              {!isPaymentPending && !hideProjectsBar && projectsLeft === 0 && (
                <div className="usage-alert usage-alert--danger">
                  You've used all your project slots. Upgrade your plan to
                  create more projects.
                </div>
              )}
              {!hideQuestionsBar &&
                questionsLeft !== null &&
                questionsLeft !== 0 &&
                questionUsagePercent < 20 &&
                questionsLeft > 0 && (
                  <div className="usage-alert usage-alert--warning">
                    You're running low on avatar questions. Consider upgrading
                    your plan.
                  </div>
                )}
              {!isPaymentPending &&
                !hideQuestionsBar &&
                questionsLeft === 0 && (
                  <div className="usage-alert usage-alert--danger">
                    You've used all your avatar questions. Upgrade your plan to
                    continue using the avatar feature.
                  </div>
                )}
            </div>

            {/* Payment History */}
            <div className="admin-card">
              <h3 className="payment-history-title">Payment History</h3>
              {paymentHistory.length === 0 ? (
                <p className="ph-empty">No payments yet.</p>
              ) : (
                <div className="ph-table-wrap">
                  <table className="ph-table">
                    <thead>
                      <tr>
                        <th scope="col">Paid by</th>
                        <th scope="col">Date</th>
                        <th scope="col">Plan</th>
                        <th scope="col" className="ph-col-amount">
                          Price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedPayments.map((payment, index) => (
                        <tr key={index}>
                          <td data-label="Paid by">
                            <span className="ph-admin">
                              <span className="ph-avatar" aria-hidden="true">
                                {payment.adminName
                                  .split(" ")
                                  .map((part) => part[0])
                                  .join("")
                                  .slice(0, 2)
                                  .toUpperCase()}
                              </span>
                              <span className="ph-admin-name">
                                {payment.adminName}
                              </span>
                            </span>
                          </td>
                          <td data-label="Date" className="ph-date">
                            {payment.date}
                          </td>
                          <td data-label="Plan">
                            <span className="ph-plan">{payment.plan}</span>
                          </td>
                          <td data-label="Price" className="ph-col-amount">
                            <span className="ph-price">
                              <span className="ph-amount">
                                {payment.amount}
                              </span>
                              <span
                                className={`ph-status ${
                                  payment.status === "paid"
                                    ? "ph-status--paid"
                                    : "ph-status--free"
                                }`}
                              >
                                {payment.status}
                              </span>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {paymentHistory.length > 5 && (
                <button
                  className="admin-btn btn-secondary"
                  style={{ width: "100%", marginTop: "0.75rem" }}
                  onClick={() => {
                    if (allPaymentsVisible) {
                      setVisiblePayments((prev) => Math.max(prev - 5, 5));
                    } else {
                      setVisiblePayments((prev) =>
                        Math.min(prev + 5, paymentHistory.length),
                      );
                    }
                  }}
                >
                  {allPaymentsVisible ? "View Less" : "View More"}
                </button>
              )}
            </div>

            <ManagePaymentModal
              isOpen={showPaymentModal}
              onClose={() => setShowPaymentModal(false)}
            />
          </>
        )}

        <PricingModal
          isOpen={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          currentPlanName={userPlan?.plan_name || ""}
        />
      </div>
    </div>
  );
};

const PlanFeature = ({ text }) => (
  <div className="plan-feature-item">
    <div className="plan-feature-dot-outer">
      <div className="plan-feature-dot-inner" />
    </div>
    <span>{text}</span>
  </div>
);

const UsageBar = ({ label, value, width, color, subtitle, icon }) => (
  <div className="usage-bar">
    <div className="usage-header">
      <div>
        <div className="usage-label-row">
          {icon && <span className="usage-icon">{icon}</span>}
          <span className="usage-label">{label}</span>
        </div>
        {subtitle && <span className="usage-subtitle">{subtitle}</span>}
      </div>
      <span className="usage-value">{value}</span>
    </div>
    <div className="usage-track">
      <div className={`usage-fill usage-fill--${color}`} style={{ width }} />
    </div>
  </div>
);

export default Pricing;
